import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { TableScroll } from '@hp/ui';
import type { ClusterFilters, ClusterListItem } from './queries';
import { EntityBadge, RuleChips, StatusBadge, TierBadge } from '../../components/badges';
import { RelativeTime } from '../../components/relative-time';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';

export function ClusterTable({
  items,
  filters,
  bulkMode,
  isLoading,
  selected,
  allSelected,
  onToggleSelected,
  onSelectAll,
}: {
  items: ClusterListItem[];
  filters: ClusterFilters;
  bulkMode: boolean;
  isLoading: boolean;
  selected: ReadonlySet<number>;
  allSelected: boolean;
  onToggleSelected: (id: number) => void;
  onSelectAll: () => void;
}) {
  const { t } = useTranslation(['review', 'common']);
  const navigate = useNavigate();
  const colCount = 8 + (bulkMode ? 1 : 0) + (filters.status !== 'pending' ? 1 : 0);

  return (
    <TableScroll label={t('table.label')}>
      <BrutTable className="bo-cluster-table">
        <BrutTheadRow>
          {bulkMode && (
            <th className="bo-cell-check">
              <input type="checkbox" checked={allSelected} onChange={onSelectAll} aria-label={t('table.selectAll')} />
            </th>
          )}
          <th>{t('common:table.id')}</th>
          <th>{t('table.entity')}</th>
          <th>{t('table.members')}</th>
          <th>{t('table.size')}</th>
          <th>{t('table.tier')}</th>
          {filters.status !== 'pending' && <th>{t('common:table.status')}</th>}
          <th>{t('table.rules')}</th>
          <th>{t('table.score')}</th>
          <th>{t('common:table.created')}</th>
        </BrutTheadRow>
        <tbody>
          {items.map((cluster: ClusterListItem) => (
            <tr
              key={cluster.id}
              className="bo-row--link"
              onClick={() => void navigate(`/crm-data/deduplication/${cluster.id}`)}
            >
              {bulkMode && (
                <td className="bo-cell-check" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(cluster.id)}
                    onChange={() => onToggleSelected(cluster.id)}
                    aria-label={t('table.selectCluster', { id: cluster.id })}
                  />
                </td>
              )}
              <td className="bo-cell-muted">{cluster.id}</td>
              <td>
                <EntityBadge entityType={cluster.entityType} />
              </td>
              <td className="bo-cell-members">
                <span className="bo-clip" title={cluster.memberNames ?? undefined}>
                  {cluster.memberNames ?? '—'}
                </span>
              </td>
              <td>{cluster.size}</td>
              <td>
                <TierBadge tier={cluster.tier} />
              </td>
              {filters.status !== 'pending' && (
                <td>
                  <StatusBadge status={cluster.status} />
                </td>
              )}
              <td>
                <RuleChips rules={cluster.rules} />
              </td>
              <td>{cluster.topScore ?? '—'}</td>
              <td>
                <RelativeTime value={cluster.createdAt} />
              </td>
            </tr>
          ))}
          {!isLoading && items.length === 0 && (
            <tr>
              <td colSpan={colCount} className="bo-cell-empty">
                {t('table.empty')}
              </td>
            </tr>
          )}
          {isLoading && (
            <tr>
              <td colSpan={colCount} className="bo-cell-empty">
                {t('common:actions.loading')}
              </td>
            </tr>
          )}
        </tbody>
      </BrutTable>
    </TableScroll>
  );
}
